import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createPlatformLicense, fetchPlatformLicenses } from "@/lib/platformApi";
import {
  billingCadenceLabel,
  commercialPlanLabel,
  commercialPlanModuleIds,
  commercialPlanOption,
  commercialPlanOptions,
} from "@/lib/commercialPlanUi";
import { resolveIssuedRequestOrganizationId } from "@/lib/platformLicenseRequestsUi";

function addYearsIso(isoDate, years = 1) {
  const base = String(isoDate || "").slice(0, 10);
  const d = base ? new Date(`${base}T12:00:00`) : new Date();
  if (Number.isNaN(d.getTime())) {
    const n = new Date();
    n.setFullYear(n.getFullYear() + years);
    return n.toISOString().slice(0, 10);
  }
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function parseDuplicateEmitError(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "object" && detail?.code === "LICENSE_EMIT_DUPLICATE") {
    return detail.message || "Ya existe una licencia activa idéntica.";
  }
  if (typeof detail === "string" && /LICENSE_EMIT_DUPLICATE|misma vigencia/i.test(detail)) {
    return detail;
  }
  return null;
}

/**
 * Diálogo diseñador: ampliar/renovar licencia de una solicitud ya emitida
 * (nueva revisión + instalación en el titular).
 */
export default function PlatformIssuedLicenseExpandDialog({
  open,
  onOpenChange,
  requestRow,
  plansCatalog,
  onDone,
}) {
  const orgId = resolveIssuedRequestOrganizationId(requestRow);
  const planOptions = useMemo(() => commercialPlanOptions(plansCatalog), [plansCatalog]);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentLicense, setCurrentLicense] = useState(null);
  const [validFrom, setValidFrom] = useState(todayIso());
  const [validTo, setValidTo] = useState(addYearsIso(todayIso(), 1));
  const [commercialPlan, setCommercialPlan] = useState("");
  const [billingCadence, setBillingCadence] = useState("annual");
  const [authorizedUsers, setAuthorizedUsers] = useState("");
  const [authorizedClients, setAuthorizedClients] = useState("");

  const selectedPlan = useMemo(
    () => commercialPlanOption(plansCatalog, commercialPlan),
    [plansCatalog, commercialPlan],
  );

  const loadCurrent = useCallback(async () => {
    if (!open || !orgId) {
      setCurrentLicense(null);
      return;
    }
    setLoading(true);
    try {
      const items = await fetchPlatformLicenses(orgId);
      const current =
        items.find((lic) => lic.is_current)
        || items.find((lic) => lic.status === "active")
        || items[0]
        || null;
      setCurrentLicense(current);
      const from = String(current?.valid_from || todayIso()).slice(0, 10);
      const toBase = String(current?.valid_to || from).slice(0, 10);
      setValidFrom(from);
      setValidTo(addYearsIso(toBase, 1));
      const planId =
        String(current?.commercial_plan || requestRow?.commercial_plan || planOptions[0]?.id || "").trim();
      setCommercialPlan(planId);
      setBillingCadence(
        String(current?.billing_cadence || requestRow?.billing_cadence || "annual").toLowerCase() === "monthly"
          ? "monthly"
          : "annual",
      );
      setAuthorizedUsers(
        current?.authorized_users != null
          ? String(current.authorized_users)
          : requestRow?.authorized_users != null
            ? String(requestRow.authorized_users)
            : "",
      );
      setAuthorizedClients(
        current?.authorized_clients != null
          ? String(current.authorized_clients)
          : requestRow?.authorized_clients != null
            ? String(requestRow.authorized_clients)
            : "",
      );
    } catch {
      toast.error("No se pudo cargar la licencia vigente de la organización");
      setCurrentLicense(null);
    } finally {
      setLoading(false);
    }
  }, [open, orgId, planOptions, requestRow]);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  const submit = async (confirmDuplicate = false) => {
    if (!orgId) {
      toast.error("La solicitud no tiene organización enlazada. Use Licencias emitidas.");
      return;
    }
    if (!selectedPlan) {
      toast.error("Seleccione un plan comercial publicado");
      return;
    }
    if (!validFrom || !validTo || validTo < validFrom) {
      toast.error("Revise las fechas de vigencia");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        organization_id: orgId,
        valid_from: validFrom.slice(0, 10),
        valid_to: validTo.slice(0, 10),
        commercial_plan: commercialPlan,
        billing_cadence: billingCadence,
        install_on_owner: true,
        confirm_duplicate: confirmDuplicate,
      };
      const u = Number(authorizedUsers);
      const c = Number(authorizedClients);
      if (Number.isFinite(u) && u >= 1) payload.authorized_users = u;
      if (Number.isFinite(c) && c >= 1) payload.authorized_clients = c;

      const lic = await createPlatformLicense(payload);
      toast.success(
        `Licencia ampliada/renovada (rev.${lic.revision}). Vigencia hasta ${lic.valid_to}.`,
      );
      onOpenChange?.(false);
      onDone?.(lic);
    } catch (err) {
      const dupMsg = parseDuplicateEmitError(err);
      if (dupMsg && !confirmDuplicate) {
        const ok = window.confirm(`${dupMsg}\n\n¿Emitir una nueva revisión de todas formas?`);
        if (ok) {
          setSubmitting(false);
          return submit(true);
        }
        return;
      }
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "object" ? detail?.message : detail)
          || "No se pudo ampliar/renovar la licencia",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="platform-issued-license-expand-dialog"
      >
        <DialogHeader>
          <DialogTitle>Ampliar / renovar licencia</DialogTitle>
        </DialogHeader>

        {!orgId ? (
          <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 m-0">
            Esta solicitud no tiene <code>organization_id</code>. Vaya a{" "}
            <strong>Licencias emitidas</strong> y seleccione la organización del comprador.
          </p>
        ) : loading ? (
          <p className="text-sm text-slate-500 inline-flex items-center gap-2 m-0">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando licencia vigente…
          </p>
        ) : (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-600 m-0 leading-relaxed">
              Crea una <strong>nueva revisión</strong> e instala el token en el titular.
              Puede cambiar vigencia, plan y cupos de usuarios/clientes.
            </p>
            {currentLicense ? (
              <div
                className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700"
                data-testid="platform-issued-expand-current"
              >
                Actual: vig. {currentLicense.valid_from} → {currentLicense.valid_to}
                {currentLicense.commercial_plan
                  ? ` · ${commercialPlanLabel(currentLicense.commercial_plan)} · ${billingCadenceLabel(currentLicense.billing_cadence)}`
                  : ""}
                {currentLicense.authorized_users != null
                  ? ` · ${currentLicense.authorized_users} usuarios`
                  : ""}
                {currentLicense.authorized_clients != null
                  ? ` / ${currentLicense.authorized_clients} clientes`
                  : ""}
                {currentLicense.revision != null ? ` · rev.${currentLicense.revision}` : ""}
              </div>
            ) : (
              <p className="text-xs text-amber-800 m-0">
                No hay licencia activa listada; se emitirá una nueva para la organización.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="expand-valid-from">Vigencia desde</Label>
                <Input
                  id="expand-valid-from"
                  type="date"
                  className="mt-1"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  data-testid="platform-issued-expand-valid-from"
                />
              </div>
              <div>
                <Label htmlFor="expand-valid-to">Vigencia hasta</Label>
                <Input
                  id="expand-valid-to"
                  type="date"
                  className="mt-1"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  data-testid="platform-issued-expand-valid-to"
                />
              </div>
            </div>

            <div>
              <Label>Plan comercial</Label>
              <Select value={commercialPlan} onValueChange={setCommercialPlan}>
                <SelectTrigger className="mt-1" data-testid="platform-issued-expand-plan">
                  <SelectValue placeholder="Seleccione plan" />
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map((plan) => (
                    <SelectItem key={plan.id} value={plan.id}>
                      {plan.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Cadencia</Label>
              <Select value={billingCadence} onValueChange={setBillingCadence}>
                <SelectTrigger className="mt-1" data-testid="platform-issued-expand-cadence">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Mensual</SelectItem>
                  <SelectItem value="annual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {selectedPlan ? (
              <p className="text-[11px] text-slate-600 m-0">
                Módulos del plan: {commercialPlanModuleIds(selectedPlan).join(" · ") || "—"}
                {" · "}Límite matriz: {selectedPlan.limits?.authorized_users ?? "—"} usuarios /{" "}
                {selectedPlan.limits?.authorized_clients ?? "—"} clientes (puede sobreescribir abajo).
              </p>
            ) : null}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="expand-users">Usuarios autorizados</Label>
                <Input
                  id="expand-users"
                  type="number"
                  min={1}
                  className="mt-1"
                  value={authorizedUsers}
                  onChange={(e) => setAuthorizedUsers(e.target.value)}
                  data-testid="platform-issued-expand-users"
                />
              </div>
              <div>
                <Label htmlFor="expand-clients">Clientes autorizados</Label>
                <Input
                  id="expand-clients"
                  type="number"
                  min={1}
                  className="mt-1"
                  value={authorizedClients}
                  onChange={(e) => setAuthorizedClients(e.target.value)}
                  data-testid="platform-issued-expand-clients"
                />
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247]"
            disabled={submitting || loading || !orgId || !selectedPlan}
            onClick={() => submit(false)}
            data-testid="platform-issued-expand-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Confirmar ampliación
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
